import { Address } from "./Address";
import { BaseEntity } from "./BaseEntity";
import { ClientExecutive } from "./ClientExecutive";
import { Country } from "./Country";
import { CustomFieldValue } from "./CustomFieldValue";
import { INSTRUCTOR_STATUS, INSTRUCTOR_TYPE } from "./Enums";
import { Language } from "./Language";
import { Partner } from "./Partner";
import { Skill } from "./Skill";

export interface Instructor extends BaseEntity {
    name: string;
    type: INSTRUCTOR_TYPE;
    certificationId: string;
    rhnId: string;
    redhatEmail: string;
    email: string;
    mobileNumber: string;
    rhcx: boolean;
    status: INSTRUCTOR_STATUS;
    activationDate: Date; 
    deactivationDate: Date;
    customFields: CustomFieldValue[];
    dailyRate: number;
    /**
     * The home address exactly as the feed sent it, kept whether or not it could be read into
     * {@link homeAddress}.
     *
     * **Read-only, and never a form control.** The service owns it: it is
     * `@JsonProperty(access = READ_ONLY)` and `InstructorResource.updateInstructor` carries the
     * stored value onto an incoming instructor before the merge, so a save cannot write it - which
     * is also why it is optional here, being absent from every request body this app sends.
     *
     * **Not a failure marker.** It is present whether the reading succeeded or not. It exists
     * because the feed sends the whole address as one block in `homeAddress.line1` - 71 of the 167
     * instructor addresses had raw newlines inside that column - so the first line is not a line,
     * it is the address, and it has to be read rather than copied. This is the record; homeAddress
     * is the reading.
     *
     * Rendered by AddressAsReceived, collapsed. During the migration window this and
     * `homeAddress.line1` hold the same blob - see that component.
     */
    homeAddressText?: string;
    homeAddress: Address;
    languages: Language[];
    nacionality: Country;
    nameLMS: string;
    partner: Partner;
    passportAuthoriCountry: Country;
    passportNumber: string;
    passportExpirationDate: Date; 
    rhciAttendanceDate: Date; 
    rhciVirtualAttendanceDate: Date; 
    skills: Skill[];
    clientExecutive: ClientExecutive | undefined;
}

/**
 * Names one of an instructor's skills from its row id alone.
 *
 * For the change log, which reports a skill as a bare id and nothing else: the audit trail labels
 * a reference only from a field called name, title, key, code, sku or fullname, and a Skill has
 * none of them. The instructor on screen carries its skills in full, so this needs no request of
 * its own.
 *
 * **It covers the whole history because a skill is never removed** - a skill that no longer
 * applies is demoted, so that the history of it survives, and the service enforces that. Every id
 * the change log can report is therefore still on the record. Rows recorded before that rule
 * return undefined here and the caller falls back to the id.
 *
 * A demotion is a change to the Skill row rather than to `Instructor.skills`, so it is that
 * entity's revision and does not appear in the instructor's own change log. Making it visible
 * means registering Skill in the service's AuditableEntity - it is not something this side can do.
 */
export const skillRefLabel = (skills: Skill[] | undefined | null, id: number): string | undefined => {
    const skill = (skills ?? []).find(candidate => candidate?.id === id);
    if (!skill) return undefined;

    // The Skills List tab's spelling minus the course name: these are chips in a narrow cell, and
    // the SKU is what identifies the skill. A certification says so instead of carrying a level,
    // which on an exam row is not the thing being reported.
    const qualifier = skill.certification ? 'certification' : skill.level;
    return [skill.course?.sku, qualifier].filter(Boolean).join(' ') || undefined;
};

/**
 * The statuses an instructor may be put on a class in: on the books, and Red Hat's own. The rest -
 * inactive, pending, never enrolled - stay out of the allocation pickers, though they are still
 * listed and edited on the Instructors page, and still searchable in Classes History, where the
 * question is who delivered a class rather than who could deliver one.
 */
export const ALLOCATABLE_STATUSES: INSTRUCTOR_STATUS[] = [INSTRUCTOR_STATUS.ACTIVE, INSTRUCTOR_STATUS.REDHAT];

export const isAllocatable = (instructor: Instructor | undefined | null): boolean =>
    ALLOCATABLE_STATUSES.includes(instructor?.status as INSTRUCTOR_STATUS);

/**
 * Who an allocation picker may offer.
 *
 * `keep` names instructors that must appear whatever their status - the ones already on the record
 * being edited. Leaving them out would not merely hide a name: MultiTypeaheadSelect reads its
 * selection off the options it was handed, so an assigned instructor missing from the list is an
 * instructor dropped by the next save. Somebody who has since gone inactive stays on the class
 * they were already allocated to until a person takes them off it.
 */
export const allocatableInstructors = (
    instructors: Instructor[] | undefined | null,
    keep: Instructor[] | undefined | null = []
): Instructor[] => {
    const kept = new Set((keep ?? []).map(instructor => instructor?.id).filter(Boolean));
    return (instructors ?? []).filter(instructor => isAllocatable(instructor) || kept.has(instructor?.id));
};
